/**
 * Storage abstraction layer.
 *
 * All app storage access goes through this module so that a future
 * Capacitor migration only requires swapping the implementation here
 * (e.g. to @capacitor/preferences) instead of hunting through the
 * entire codebase.
 *
 * Two storage tiers:
 *  - KV store   : lightweight key-value pairs (flags, settings)
 *  - Bulk store : heavy data (Redux persist / game archive)
 *
 * Session-scoped storage is also provided for ephemeral per-session
 * flags (e.g. splash screen shown).
 */

import localforage from 'localforage';

// ── KV Store (lightweight key-value pairs) ──────────────────────────
//
// Async versions — preferred API. When migrating to Capacitor, only
// these need to change (swap to @capacitor/preferences).
//
// Sync versions — used by code that reads in render paths or
// initializers where await isn't possible. On Capacitor these will
// need to be replaced with a preloaded cache pattern.

/** Async: get a string value by key. */
export async function kvGet(key: string): Promise<string | null> {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Async: set a string value by key. */
export async function kvSet(key: string, value: string): Promise<void> {
  try {
    localStorage.setItem(key, value);
  } catch {}
}

/** Async: remove a key. */
export async function kvRemove(key: string): Promise<void> {
  try {
    localStorage.removeItem(key);
  } catch {}
}

/** Sync: get a string value by key. */
export function kvGetSync(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Sync: set a string value by key. */
export function kvSetSync(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {}
}

// ── Session Store (per-session only, never persisted) ───────────────

/**
 * Get a session-scoped value. Cleared when the browser tab closes.
 */
export function sessionGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Set a session-scoped value.
 */
export function sessionSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // fail silently
  }
}

// ── Bulk Store (heavy data — Redux persist) ─────────────────────────

/**
 * The bulk storage driver used by redux-persist.
 * Currently backed by localforage (IndexedDB).
 *
 * When migrating to Capacitor, replace this with a custom driver
 * backed by @capacitor/preferences or the Filesystem API for
 * large payloads.
 */
export const bulkStorage = localforage;

// ── Persistence request ─────────────────────────────────────────────

/**
 * Request the browser to persist storage (prevents eviction).
 * On Capacitor this is a no-op (native storage is always persistent).
 */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (navigator.storage && navigator.storage.persist) {
      return await navigator.storage.persist();
    }
  } catch {
    // not supported
  }
  return false;
}

/**
 * Check if storage is already persisted.
 */
export async function isPersisted(): Promise<boolean> {
  try {
    if (navigator.storage && navigator.storage.persisted) {
      return await navigator.storage.persisted();
    }
  } catch {
    // not supported
  }
  return false;
}
